import { FC } from 'react';
import { useFragment } from 'react-relay';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import graphql from 'babel-plugin-relay/macro';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import EntityTableRow, { EntityTableRowProps } from 'components/EntityTableRow';

import { SortableEntityTableRow_entity$key } from '__generated__/SortableEntityTableRow_entity.graphql';

type Props = EntityTableRowProps & {
  entity: SortableEntityTableRow_entity$key;
  sortDisabled?: boolean;
};

export type SortableEntityTableRowProps = Omit<Props, 'entity'>;

const SortableEntityTableRow: FC<Props> = props => {
  const { children, entity, sortDisabled, ...rest } = props;

  const entityData = useFragment(
    graphql`
      fragment SortableEntityTableRow_entity on Entity {
        id

        ...EntityTableRow_entity
      }
    `,
    entity
  );

  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: entityData.id, disabled: sortDisabled });

  return (
    <EntityTableRow
      entity={entityData}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      ref={setNodeRef}
      {...attributes}
      {...rest}>
      <TableCell>
        <Box
          {...listeners}
          ref={setActivatorNodeRef}
          sx={{
            cursor: sortDisabled ? 'auto' : 'row-resize',
            color: sortDisabled ? 'text.disabled' : 'text.primary',
          }}>
          <DragIndicatorIcon />
        </Box>
      </TableCell>

      {children}
    </EntityTableRow>
  );
};

export default SortableEntityTableRow;
